import { useContext, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, ChevronDown, CircleDot, Heart, MessageCircle, Mountain, Quote, Tent, X } from "lucide-react";
import { EASE, Marquee, Reveal, SectionHeading, WhatsAppButton, useWhatsApp } from "../components/Shared";
import Seo from "../components/Seo";
import { LangContext } from "../i18n";
import { EVENTS } from "../data/destinations";

export default function HomePage() {
  const { lang, copy } = useContext(LangContext);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [openTour, setOpenTour] = useState(null);
  const [faq, setFaq] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const heroRef = useRef(null);
  const openWhatsApp = useWhatsApp();

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const heroMarkY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  const scrollTo = (hash) => (e) => {
    e.preventDefault();
    if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -72 });
    else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main>
      <Seo
        title={lang === "hi" ? "तीर्थाटन | वाराणसी से कुंभ 2027–28 आवास और तीर्थ यात्राएँ" : "Teerthaatan | Kumbh Mela 2027–28 Stays & Pilgrimage Tours from Varanasi"}
        description={copy.hero.body}
        path="/"
      />

      <section id="home" className="hero section-pad" ref={heroRef}>
        <motion.span className="hero-watermark" style={{ y: heroMarkY }} aria-hidden="true">तीर्थाटन</motion.span>
        <div className="hero-copy">
          <motion.p className="eyebrow" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1, ease: EASE }}>{copy.hero.eyebrow}</motion.p>
          <h1 className="hero-title" key={lang}>
            {copy.hero.lines.map((line, i) => (
              <span className="mask-line" key={i}>
                <motion.span className={line.accent ? "accent" : ""} initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.25 + i * 0.16, ease: EASE }}>{line.t}</motion.span>
              </span>
            ))}
          </h1>
          <motion.p className="hero-subline" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7, ease: EASE }}>{copy.hero.body}</motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85, ease: EASE }}>
            <WhatsAppButton testid="hero-whatsapp-button" onClick={() => openWhatsApp("general")}>{copy.hero.primary}</WhatsAppButton>
            <a className="button button-outline" href="#tours" onClick={scrollTo("#tours")} data-testid="hero-explore-link">{copy.hero.secondary}<ArrowDown size={16} /></a>
          </motion.div>
          <motion.div className="hero-note" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }}>
            <span className="ornament">✦</span><span>{copy.hero.note}</span>
          </motion.div>
        </div>
        <motion.div className="hero-visual" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.4, ease: EASE }}>
          <div className="arch-frame" data-testid="hero-image-frame">
            <motion.img src="/assets/guru-maa.jpg" alt={copy.hero.imageLabel} style={{ y: heroImgY }} />
            <div className="arch-caption"><span>तीर्थाटन</span><span>Varanasi</span></div>
          </div>
          <div className="arch-ring" aria-hidden="true" />
        </motion.div>
      </section>

      <Marquee />

      <section className="trust-strip" data-testid="trust-strip">
        {copy.trust.map((item, i) => (
          <Reveal key={item} delay={i * 0.08} className="trust-item">
            <div data-testid={`trust-item-${i}`}><span>0{i + 1}</span><p>{item}</p></div>
          </Reveal>
        ))}
      </section>

      <section id="about" className="section-pad about-section">
        <Reveal className="about-visual">
          <div className="about-frame">
            <img src="/assets/guru-maa.jpg" alt={copy.about.imageLabel} loading="lazy" data-testid="about-image" />
            <div className="portrait-seal">ॐ<br /><small>सेवा</small></div>
          </div>
          <blockquote className="about-quote"><Quote size={18} /><span>{copy.about.quote}</span></blockquote>
        </Reveal>
        <div className="about-copy">
          <SectionHeading data={copy.about} />
          <AnimatePresence initial={false}>
            {aboutOpen && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: EASE }} style={{ overflow: "hidden" }}>
                <p className="body-copy">{copy.about.more}</p>
              </motion.div>
            )}
          </AnimatePresence>
          <button className="text-button" onClick={() => setAboutOpen(!aboutOpen)} data-testid="about-read-more-button">
            {aboutOpen ? copy.about.less : copy.about.read}<span>{aboutOpen ? "−" : "+"}</span>
          </button>
        </div>
      </section>

      <section id="tours" className="section-pad tours-section">
        <SectionHeading data={copy.tours} />
        <Reveal className="tours-feature">
          <figure className="feature-figure">
            <img src="/assets/kumbh-tent-4.jpg" alt={copy.tours.featureCaption} loading="lazy" data-testid="tours-feature-image" />
            <figcaption><span className="ornament">✦</span>{copy.tours.featureCaption}</figcaption>
          </figure>
        </Reveal>
        <div className="tour-grid">
          {copy.tours.cards.map(([title, description, key], i) => (
            <Reveal key={key} delay={(i % 3) * 0.08} className={`tour-card-wrap ${i === 0 ? "featured" : ""}`}>
              <article className="tour-card" data-testid={`tour-card-${key}`}>
                {key === "kumbh" ? (
                  <Link to="/kumbh" className="card-photo" data-testid="tour-kumbh-photo-link"><img src="/assets/kumbh-tent-1.jpg" alt={title} loading="lazy" /></Link>
                ) : (
                  <div className={`card-art art-${key}`}><span>{String(i + 1).padStart(2, "0")}</span><Mountain size={20} /></div>
                )}
                <div className="card-content">
                  <h3>{title}</h3>
                  <p>{description}</p>
                  {key === "jyotirlinga" && <div className="jyotirlinga-list">{copy.tours.jyotirlingas.map((name) => <span key={name}>{name}</span>)}</div>}
                  <div className="card-actions">
                    {key === "kumbh" ? (
                      <Link to="/kumbh" className="text-button" data-testid="tour-kumbh-explore">{copy.kumbhBanner.primary}<span>→</span></Link>
                    ) : (
                      <button className="text-button" onClick={() => setOpenTour(openTour === key ? null : key)} data-testid={`tour-details-${key}`}>
                        {openTour === key ? copy.tours.hide : copy.tours.know}<span>{openTour === key ? "−" : "+"}</span>
                      </button>
                    )}
                    <button className="mini-wa" onClick={() => openWhatsApp(key)} data-testid={`tour-whatsapp-${key}`}><MessageCircle size={15} /> WhatsApp</button>
                  </div>
                  <AnimatePresence initial={false}>
                    {openTour === key && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: EASE }} style={{ overflow: "hidden" }}>
                        <div className="tour-detail">{key === "jyotirlinga" ? copy.tours.jyotirlingas.join(" · ") : description}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal className="kumbh-banner" data-testid="kumbh-banner">
          <img src="/assets/yagya-shala.jpg" alt={lang === "hi" ? "रात में जगमगाया कुंभ अखाड़ा पंडाल" : "The akhada pavilion lit at night during Kumbh"} loading="lazy" />
          <div className="kumbh-banner-inner">
            <p className="eyebrow">{copy.kumbhBanner.kicker}</p>
            <h3>{copy.kumbhBanner.title}</h3>
            <p>{copy.kumbhBanner.body}</p>
            <div className="hero-actions">
              <Link to="/kumbh" className="button" data-testid="kumbh-banner-explore"><Tent size={16} /> {copy.kumbhBanner.primary}</Link>
              <button className="button button-outline light" onClick={() => openWhatsApp("kumbh")} data-testid="kumbh-banner-whatsapp"><MessageCircle size={16} /> {copy.kumbhBanner.secondary}</button>
            </div>
          </div>
        </Reveal>

        <Reveal className="dates-banner">
          <div><p className="eyebrow">{copy.tours.dates}</p><h3>{copy.tours.datesBody}</h3></div>
          <WhatsAppButton testid="dates-whatsapp-button" outline onClick={() => openWhatsApp("general")}>{copy.tours.ask}</WhatsAppButton>
        </Reveal>
      </section>

      <section className="section-pad events-section">
        <SectionHeading data={copy.events} />
        <div className="event-row">
          {EVENTS.map((ev, i) => (
            <Reveal key={ev.id} delay={i * 0.08} className="event-card-wrap">
              <Link to={`/kumbh/${ev.city}`} className="event-card" data-testid={`event-card-${ev.id}`}>
                <span className={`status-badge ${ev.status}`}>{ev.status === "confirmed" ? (lang === "hi" ? "तिथियाँ घोषित" : "Dates confirmed") : ev.status === "tentative" ? (lang === "hi" ? "अपेक्षित" : "Expected") : (lang === "hi" ? "वार्षिक" : "Annual")}</span>
                <p className="event-city">{ev[lang].city}</p>
                <h3>{ev[lang].title}</h3>
                <p className="event-dates">{ev[lang].dates}</p>
                <p className="event-note">{ev[lang].note}</p>
                <span className="event-link">{copy.events.link} <ArrowUpRight size={14} /></span>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal><p className="events-note">{copy.events.note}</p></Reveal>
      </section>

      <section id="vedamrit" className="dark-section section-pad">
        <SectionHeading data={copy.vedamrit} dark />
        <Reveal className="vedic-visuals">
          <figure className="vedic-figure"><img src="/assets/yoga-1.jpg" alt={copy.vedamrit.imgAlts.yoga} loading="lazy" data-testid="vedamrit-yoga-image" /></figure>
          <figure className="vedic-figure offset"><img src="/assets/yagya.jpg" alt={copy.vedamrit.imgAlts.yagya} loading="lazy" data-testid="vedamrit-yagya-image" /></figure>
        </Reveal>
        <div className="vedic-grid">
          {copy.vedamrit.cards.map(([title, description, icon], i) => (
            <Reveal key={title} delay={(i % 4) * 0.06} className="vedic-card-wrap">
              <article className="vedic-card" data-testid={`vedamrit-card-${i}`}>
                <span className="vedic-icon">{icon}</span><h3>{title}</h3><p>{description}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <div className="center-action"><WhatsAppButton testid="vedamrit-whatsapp-button" onClick={() => openWhatsApp("vedamrit")}>{copy.vedamrit.ask}</WhatsAppButton></div>
      </section>

      <section id="seva" className="section-pad seva-section">
        <SectionHeading data={copy.seva} />
        <div className="seva-bento">
          {copy.seva.cards.map(([title, description, img, alt, kind], i) => (
            <Reveal key={title} delay={i * 0.07} className={`seva-cell seva-${kind}`}>
              <article className="seva-card" data-testid={`seva-card-${i}`}>
                {img && <div className="seva-photo"><img src={img} alt={alt} loading="lazy" /></div>}
                <div className="seva-body">
                  <span className="seva-num">{String(i + 1).padStart(2, "0")}</span>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <div className="center-action"><WhatsAppButton testid="seva-whatsapp-button" onClick={() => openWhatsApp("seva")}>{copy.seva.ask}</WhatsAppButton></div>
      </section>

      <section className="steps-section section-pad">
        <SectionHeading data={copy.how} />
        <div className="steps-grid">
          {copy.how.steps.map(([number, title, body], i) => (
            <Reveal key={number} delay={i * 0.1} className="step-wrap">
              <article className="step" data-testid={`how-step-${number}`}>
                <span className="step-num">{number}</span>
                <div><h3>{title}</h3><p>{body}</p></div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="faq-section section-pad">
        <SectionHeading data={copy.faq} />
        <div className="faq-list">
          {copy.faq.items.map(([question, answer], i) => (
            <Reveal key={question} delay={i * 0.05}>
              <div className={`faq-item ${faq === i ? "open" : ""}`}>
                <button onClick={() => setFaq(faq === i ? null : i)} data-testid={`faq-question-${i}`}>
                  <span>{question}</span>
                  <motion.span animate={{ rotate: faq === i ? 180 : 0 }} transition={{ duration: 0.3 }}><ChevronDown size={18} /></motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {faq === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: EASE }} style={{ overflow: "hidden" }}>
                      <p data-testid={`faq-answer-${i}`}>{answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="gallery" className="gallery-section section-pad">
        <SectionHeading data={copy.gallery} />
        <Reveal><p className="gallery-subtitle">{copy.gallery.subtitle}</p></Reveal>
        <div className="gallery-grid">
          {copy.gallery.photos.map(([src, caption], i) => (
            <Reveal key={src} delay={(i % 3) * 0.07} className="gallery-tile-wrap">
              <button className="gallery-tile" onClick={() => setLightbox({ src, caption })} data-testid={`gallery-item-${i}`}>
                <img src={src} alt={caption} loading="lazy" />
                <span className="gallery-caption">{caption}<ArrowUpRight size={15} /></span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="contact" className="contact-section section-pad">
        <div className="contact-copy">
          <SectionHeading data={copy.contact} dark />
          <Reveal delay={0.1}><p className="body-copy">{copy.contact.body}</p></Reveal>
          <Reveal delay={0.15}><p className="address" data-testid="contact-address"><CircleDot size={17} />{copy.contact.address}</p></Reveal>
          <Reveal delay={0.2}><WhatsAppButton testid="contact-whatsapp-button" onClick={() => openWhatsApp("general")}>{copy.contact.whatsapp}</WhatsAppButton></Reveal>
          <p className="personal-note"><Heart size={14} fill="currentColor" /> {copy.contact.note}</p>
        </div>
        <Reveal className="map-wrap" delay={0.15}>
          <iframe title="Teerthaatan location in Varanasi" src="https://www.google.com/maps?q=8XPW%2B4J+Varanasi&output=embed" loading="lazy" data-testid="contact-map" />
        </Reveal>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div className="lightbox" role="dialog" aria-modal="true" onClick={() => setLightbox(null)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} data-testid="gallery-lightbox">
            <motion.figure className="lightbox-card" onClick={(e) => e.stopPropagation()} initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 12 }} transition={{ duration: 0.35, ease: EASE }}>
              <button className="lightbox-close" onClick={() => setLightbox(null)} data-testid="gallery-lightbox-close"><X /></button>
              <img src={lightbox.src} alt={lightbox.caption} data-testid="lightbox-image" />
              <figcaption>{lightbox.caption} · Teerthaatan</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
